import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from '../../firebase';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, MagnifyingGlassIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';
import { formatDate } from '../Help/helper';


const pages = [
  { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
  { name: 'Closed Job Cards', to: '/vehicles/closedjobs', component: Link, current: true },  
];



const ClosedJobs = () => {
  const [jobs, setJobs] = useState([]);
  const { role } = useAuth();
  const [sortConfig, setSortConfig] = useState({ key: 'jobCardNo', direction: 'ascending' });
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);
  const navigate = useNavigate();
  const [searchCategory, setSearchCategory] = useState('customerName');
  const [searchQuery, setSearchQuery] = useState('');
 

  useEffect(() => {
    fetchJobs();
  }, []);
 

  const fetchJobs = async () => {
    setIsLoading(true);
    const jobsCollection = collection(db, 'jobs');
    const openJobsQuery = query(jobsCollection, where('status', '==', 'Closed'));
    const jobsSnapshot = await getDocs(openJobsQuery);
    const jobsList = await Promise.all(
      jobsSnapshot.docs.map(async (docSnapshot) => {
        const jobData = docSnapshot.data();
        return {
          ...jobData,
          id: docSnapshot.id,
        };
      })
    );
    setJobs(jobsList);
    setIsLoading(false);
  };

  const handleSearch = async () => {
    if (!searchQuery) {
      fetchJobs();
      return;
    }

    setIsLoading(true);

    const jobsCollection = collection(db, 'jobs');
    let searchQueryRef;

    if (searchCategory === 'customerName') {
      searchQueryRef = query(jobsCollection, where('customerName', '>=', searchQuery), where('customerName', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'VIN') {
      searchQueryRef = query(jobsCollection, where('VIN', '>=', searchQuery), where('VIN', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'jobCardNo') {
      searchQueryRef = query(jobsCollection, where('jobCardNo', '>=', searchQuery), where('jobCardNo', '<=', searchQuery + '\uf8ff'));
    } else if (searchCategory === 'registrationNumber') {
      searchQueryRef = query(jobsCollection, where('registrationNumber', '>=', searchQuery), where('registrationNumber', '<=', searchQuery + '\uf8ff'));
    }

    const jobsSnapshot = await getDocs(searchQueryRef);
    const jobsList = jobsSnapshot.docs.map((docSnapshot) => ({
      id: docSnapshot.id,
      ...docSnapshot.data(),
    }));

    setJobs(jobsList);
    setIsLoading(false);
  };

  const sortData = (key, direction) => {
    setSortConfig({
      key: key,
      direction: direction
    });
  };

  const sortedJobs = () => {
    if (!sortConfig) return jobs;
    return [...jobs].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const currentJobs = sortedJobs().slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage);
  const totalPages = Math.ceil(jobs.length / jobsPerPage);
  const changePage = (pageNumber) => setCurrentPage(pageNumber);

  const navigateToJobDetail = (jobCardNo) => {
    navigate(`/vehicles/jobupdate/${jobCardNo}`);
  };

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }


  return (
    <>
      <BreadCrumb pages={pages} />

      {/* Gradient Background */}
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 py-4">
        <div className="container mx-auto px-4">

          {/* Page Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl shadow-lg">
                <CheckCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900">Closed Job Cards</h1>
                <p className="text-sm text-gray-600 mt-0.5">View completed and archived service records</p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl shadow-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-sm font-semibold uppercase tracking-wide">Total Closed Jobs</p>
                  <p className="text-4xl font-black mt-2">{jobs.length}</p>
                </div>
                <CheckCircleIcon className="h-16 w-16 text-rose-200 opacity-50" />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-lg">
              <div className="text-center">
                <MoonLoader size={60} color="#F43F5E" />
                <p className="mt-4 text-gray-600 font-medium">Loading closed jobs...</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">

              {/* Search Controls */}
              <div className="p-6 bg-gradient-to-r from-gray-50 to-rose-50 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <select
                    value={searchCategory}
                    onChange={(e) => setSearchCategory(e.target.value)}
                    className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="customerName">Customer Name</option>
                    <option value="VIN">VIN</option>
                    <option value="jobCardNo">Job Card No</option>
                    <option value="registrationNumber">Registration No</option>
                  </select>

                  <div className="flex-1 relative">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={`Search by ${searchCategory}...`}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  <button
                    onClick={handleSearch}
                    className="px-6 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-pink-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                    Search
                  </button>
                </div>
              </div>

              {/* Table */}
              {currentJobs.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Closed Jobs Found</h3>
                  <p className="text-gray-600">There are currently no closed job cards to display.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-rose-500 to-pink-600">
                      <tr>
                        {[
                          { label: 'Job No', key: 'jobCardNo' },
                          { label: 'Customer Name', key: 'customerName' },
                          { label: 'Reg No', key: 'registrationNumber' },
                          { label: 'VIN', key: 'VIN' },
                          { label: 'Vehicle Type', key: 'vehicleMake' },
                          { label: 'Date Created', key: 'createdAt' },
                        ].map((header) => (
                          <th
                            key={header.key}
                            className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-rose-600 transition-colors duration-150"
                            onClick={() => {
                              const newDirection = sortConfig.key === header.key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
                              sortData(header.key, newDirection);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {header.label}
                              {sortConfig.key === header.key && (
                                sortConfig.direction === 'ascending'
                                  ? <ChevronUpIcon className="h-4 w-4" />
                                  : <ChevronDownIcon className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                        ))}
                        <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {currentJobs.map((job, index) => (
                        <tr
                          key={job.id}
                          className="hover:bg-rose-50 transition-colors duration-150 cursor-pointer"
                          onClick={() => navigateToJobDetail(job.jobCardNo)}
                        >
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs font-bold text-rose-600">{job.jobCardNo}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs font-medium text-gray-900">{job.customerName}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{job.registrationNumber}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{job?.VIN}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{`${job.vehicleMake} ${job.vehicleModel}`}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{formatDate(job.createdAt.toDate())}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              Closed
                            </span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToJobDetail(job.jobCardNo);
                              }}
                              className="p-2 text-rose-600 hover:bg-rose-100 rounded-lg transition-all duration-200 hover:shadow-md"
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {currentJobs.length > 0 && totalPages > 0 && (
                <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-rose-50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">
                        Showing {(currentPage - 1) * jobsPerPage + 1}–{Math.min(currentPage * jobsPerPage, jobs.length)} of {jobs.length}
                      </span>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                        <button
                          key={n}
                          onClick={() => changePage(n)}
                          className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 ${
                            n === currentPage
                              ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white border-transparent shadow-md'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-rose-50'
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};


export default ClosedJobs;

